////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// I. FILE HEADER [WHITE #FFFFFF]
// ⚪ Library Lobby Pea for Taytrom Publishing Co - Pure presentation component

// III. IMPORTS [GREEN #00FF00]
// 🟢 Third-party dependencies
import { FC } from 'react';
import styles from './TaytromPublishingCo_Kings_Home_SRC_Library_Lobby_Pea.module.css';

// IV. INDEPENDENT CONSTANTS [TEAL #00FFFF]
// ═══════════════════════════════════════════════════════════════
// DEFINITIONS (D) - TYPE DEFINITIONS (PEA COMPONENT)
// ═══════════════════════════════════════════════════════════════

// D: VSubVSoFSt (Variable Sub/Source, Fixed Structure) - Library Book interface
interface INT_STR_IIS_D_G_DVT_R_IRO_LibraryBook {
  id: string;
  title: string;
  client: string;
  description: string;
  pageCount: number;
  publishDate: string;
  status: 'published' | 'in-progress' | 'coming-soon';
  readLink?: string;
}

// D: VSubVSoFSt (Variable Sub/Source, Fixed Structure) - Pea component props
interface INT_STR_IIS_D_G_DVT_R_IRO_PeaLibraryLobbyProps {
  books: INT_STR_IIS_D_G_DVT_R_IRO_LibraryBook[];
  selectedBookId: string | null;
  _isLoading: boolean;
  viewMode: 'catalog' | 'viewing';
  viewingBookId: string | null;
  bookHtmlUrl: string | null;
  searchQuery: string;
  filterStatus: 'all' | 'published' | 'coming-soon';
  previewBookId: string | null;
  onBookSelect: (bookId: string) => void;
  onReadBook: (bookId: string) => void;
  onBackToHome: () => void;
  onSearchChange: (query: string) => void;
  onFilterChange: (status: 'all' | 'published' | 'coming-soon') => void;
  onPreviewOpen: (bookId: string) => void;
  onPreviewClose: () => void;
  className?: string;
}

// V. FUNCTION/CLASS NAME [BLUE #0000FF] - OUTPUT DECLARATION
// 🔵 Library Lobby Pea Component
const Pea_Library_Lobby: FC<INT_STR_IIS_D_G_DVT_R_IRO_PeaLibraryLobbyProps> = ({
  books,
  selectedBookId,
  _isLoading,
  viewMode,
  viewingBookId,
  bookHtmlUrl,
  searchQuery,
  filterStatus,
  previewBookId,
  onBookSelect,
  onReadBook,
  onBackToHome,
  onSearchChange,
  onFilterChange,
  onPreviewOpen,
  onPreviewClose,
  className
}) => {
  // X. RENDER/EXPORTS [BLACK #000000] - OUTPUT DELIVERY

  // Helper: Get preview book data
  const previewBook = previewBookId ? books.find(b => b.id === previewBookId) : null;

  // Helper: Skeleton Book Card
  const SkeletonBookCard = () => (
    <div className={`${styles.libraryBookCard} ${styles.skeletonCard}`} aria-hidden="true">
      <div className={styles.skeletonBadge}></div>
      <div className={styles.skeletonIcon}></div>
      <div className={styles.skeletonTitle}></div>
      <div className={styles.skeletonClient}></div>
      <div className={styles.skeletonDescription}></div>
      <div className={styles.skeletonDescription}></div>
      <div className={styles.skeletonMetadata}>
        <div className={styles.skeletonMetaItem}></div>
        <div className={styles.skeletonMetaItem}></div>
      </div>
      <div className={styles.skeletonButton}></div>
    </div>
  );

  // Conditional rendering: Show book viewer OR catalog
  if (viewMode === 'viewing' && bookHtmlUrl && viewingBookId) {
    const viewingBook = books.find(b => b.id === viewingBookId);
    const bookTitle = viewingBook ? viewingBook.title : 'Book';

    return (
      <div className={`${styles.libraryContainer} ${className || ''}`}>
        {/* Header with Back to Library Button */}
        <header className={styles.bookViewerHeader} role="banner">
          <button
            onClick={() => window.location.href = '/library'}
            className={styles.backToLibraryButton}
            aria-label="Return to library catalog"
          >
            ← Back to Library
          </button>
        </header>

        {/* Iframe Book Viewer */}
        <div className={styles.bookIframeContainer} role="main" aria-label={`Reading ${bookTitle}`}>
          <iframe
            src={bookHtmlUrl}
            title={`${bookTitle} - Full book content`}
            className={styles.bookIframe}
            aria-label={`${bookTitle} book viewer`}
          />
        </div>
      </div>
    );
  }

  // Default: Catalog View
  return (
    <div className={`${styles.libraryContainer} ${className || ''}`}>
      {/* Screen Reader Live Region for Announcements */}
      <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
        {selectedBookId && `Selected: ${books.find(b => b.id === selectedBookId)?.title || ''}`}
      </div>

      {/* Header Section with Back Button */}
      <header className={styles.libraryHeader} role="banner">
        <button
          onClick={onBackToHome}
          className={styles.backButton}
          aria-label="Navigate back to home page"
        >
          ← Back to Home
        </button>
        <h1 className={styles.libraryTitle}>Taytrom Publishing Co. Library</h1>
        <p className={styles.librarySubtitle}>
          Comprehensive Third-Party Analysis Books
        </p>

        {/* Search and Filter Controls */}
        <div className={styles.searchFilterContainer}>
          <div className={styles.searchInputWrapper}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search books by title, client, or description..."
              className={styles.searchInput}
              aria-label="Search books"
            />
            {searchQuery && (
              <button
                onClick={() => onSearchChange('')}
                className={styles.clearSearchButton}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <div className={styles.filterButtonGroup} role="group" aria-label="Filter books by status">
            <button
              onClick={() => onFilterChange('all')}
              className={`${styles.filterButton} ${filterStatus === 'all' ? styles.filterButtonActive : ''}`}
              aria-pressed={filterStatus === 'all'}
            >
              All Books
            </button>
            <button
              onClick={() => onFilterChange('published')}
              className={`${styles.filterButton} ${filterStatus === 'published' ? styles.filterButtonActive : ''}`}
              aria-pressed={filterStatus === 'published'}
            >
              Published
            </button>
            <button
              onClick={() => onFilterChange('coming-soon')}
              className={`${styles.filterButton} ${filterStatus === 'coming-soon' ? styles.filterButtonActive : ''}`}
              aria-pressed={filterStatus === 'coming-soon'}
            >
              Coming Soon
            </button>
          </div>
        </div>
      </header>

      {/* Books Grid Section */}
      <section className={styles.libraryBooksSection} role="main" aria-label="Books catalog">
        <h2 className={styles.sectionTitle}>
          {searchQuery ? `Search Results (${books.length})` : filterStatus === 'all' ? 'All Books' : filterStatus === 'published' ? 'Published Books' : 'Coming Soon'}
        </h2>

        {_isLoading ? (
          <div className={styles.booksGrid} role="list" aria-busy="true" aria-label="Loading books">
            {[...Array(6)].map((_, index) => (
              <SkeletonBookCard key={`skeleton-${index}`} />
            ))}
          </div>
        ) : books.length === 0 ? (
          <div className={styles.noResultsMessage}>
            <p>No books found matching your search criteria.</p>
            {searchQuery && (
              <button onClick={() => onSearchChange('')} className={styles.clearFiltersButton}>
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <div className={styles.booksGrid} role="list">
            {books.map((book) => (
              <div
                key={book.id}
                className={`${styles.libraryBookCard} ${selectedBookId === book.id ? styles.selected : ''}`}
                onClick={() => onBookSelect(book.id)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onBookSelect(book.id);
                  }
                }}
                role="listitem"
                tabIndex={0}
                aria-label={`${book.title} by ${book.client}. ${book.pageCount} pages. Published ${book.publishDate}. ${selectedBookId === book.id ? 'Currently selected.' : 'Press Enter to select.'}`}
                aria-pressed={selectedBookId === book.id}
              >
                <div className={`${styles.bookStatusBadge} ${styles.published}`} aria-label="Published status">Published</div>
                <h3 className={styles.bookTitle}>{book.title}</h3>
                <p className={styles.bookClient}>Client: {book.client}</p>
                <p className={styles.bookDescription}>{book.description}</p>
                <div className={styles.bookMetadata}>
                  <span aria-label={`${book.pageCount} pages`}>{book.pageCount} pages</span>
                  <span aria-label={`Published ${book.publishDate}`}>{book.publishDate}</span>
                </div>
                <div className={styles.bookCardActions}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreviewOpen(book.id);
                    }}
                    className={styles.previewButton}
                    aria-label={`Preview ${book.title}`}
                  >
                    Quick Preview
                  </button>
                  {book.readLink && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onReadBook(book.id);
                      }}
                      className={styles.readButton}
                      aria-label={`Read ${book.title}`}
                    >
                      Read Book →
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Preview Modal */}
      {previewBook && (
        <div
          className={styles.modalBackdrop}
          onClick={onPreviewClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby="preview-modal-title"
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2 id="preview-modal-title" className={styles.modalTitle}>{previewBook.title}</h2>
              <button
                onClick={onPreviewClose}
                className={styles.modalCloseButton}
                aria-label="Close preview modal"
              >
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={`${styles.bookStatusBadge} ${previewBook.status === 'published' ? styles.published : styles.comingSoon}`}>
                {previewBook.status === 'published' ? 'Published' : 'Coming Soon'}
              </div>
              <p className={styles.modalClient}>Client: {previewBook.client}</p>
              <p className={styles.modalDescription}>{previewBook.description}</p>

              <div className={styles.modalMetadata}>
                <div className={styles.modalMetaItem}>
                  <span className={styles.modalMetaLabel}>Pages:</span>
                  <span className={styles.modalMetaValue}>{previewBook.pageCount}{previewBook.status === 'coming-soon' ? ' (est.)' : ''}</span>
                </div>
                <div className={styles.modalMetaItem}>
                  <span className={styles.modalMetaLabel}>Published:</span>
                  <span className={styles.modalMetaValue}>{previewBook.publishDate}</span>
                </div>
                <div className={styles.modalMetaItem}>
                  <span className={styles.modalMetaLabel}>Status:</span>
                  <span className={styles.modalMetaValue}>{previewBook.status === 'published' ? 'Available Now' : 'Coming Soon'}</span>
                </div>
              </div>

              {previewBook.readLink && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onPreviewClose();
                    onReadBook(previewBook.id);
                  }}
                  className={styles.modalReadButton}
                  aria-label={`Read ${previewBook.title}`}
                >
                  Read Full Book →
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className={styles.libraryFooter} role="contentinfo">
        <p>© 2025 Taytrom Publishing Co. All rights reserved.</p>
        <p>Comprehensive third-party analysis books for the AI era.</p>
      </footer>
    </div>
  );
};

export default Pea_Library_Lobby;
